import { Col, Container, Row } from 'react-bootstrap'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AES, enc } from 'crypto-js'
import {
  GET_CUSTOMER_INFO_BY_PINCODE_D2D,
  SEND_CONTRACT_AFTER_QC_APPROVE,
  SUBMIT_CONTRACT,
  GET_CLIENT_ALLOWED_COMPONENTS,
} from '../../../utils/GraphQLAPI'
import { useLazyQuery, useMutation } from '@apollo/client'
import { KTCard } from '../../../_metronic/helpers'
import { useGeolocated } from 'react-geolocated'
import { ImagePreview } from '../../sharedComponent/Preview'
import { getCustomerBypincodeData } from './Contract.types'
import PdfViewer from '../../sharedComponent/viewer/PDFViewer'
import { checkEmptyData, isValidURL, uploadFiles } from '../../../utils/helper'
import SignatureCanvas from 'react-signature-canvas'
import { renderFileHeader } from '../../../utils/Contract/ContractUtils'
import SkeletonLoader from '../../sharedComponent/Skeleton'
import { GLOBAL_CONSTANTS } from '../../../utils/GlobalConstants'
import moment from 'moment'
import momentTimezone from 'moment-timezone'

const Contract = () => {
  const [queryParams] = useSearchParams()
  const [loading, setLoading] = useState<boolean>(false)
  const [DecryptedData, setdecryptedData] = useState<any>()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>('')
  const [data, setData] = useState<getCustomerBypincodeData>({} as getCustomerBypincodeData)
  const [isSigned, setIsSigned] = useState<boolean>(false)
  const [consentChecked, setIsConsentChecked] = useState<boolean>(false)
  const [optinChecked, setOptInChecked] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState(() => false)
  const [PowerUp, setPowerUp] = useState<any>(undefined)

  //queries
  const [getCustomerInfo, { loading: dataLoading }] = useLazyQuery(GET_CUSTOMER_INFO_BY_PINCODE_D2D, {
    onCompleted: (data) => {
      setData(data?.getCustomerInfoByPincode)
      setLoading(false)
    },
    onError: (err) => setErrorMsg(err.message),
  })

  const [sendContract] = useMutation(SEND_CONTRACT_AFTER_QC_APPROVE)

  const [submitContract] = useMutation(SUBMIT_CONTRACT, {
    onCompleted: (data) => {
      setSuccessMsg('Contract signed successfully, Thank you')
      sendContract({
        variables: {
          pincode: DecryptedData.pincode,
          tpvId: DecryptedData?.tpvId,
          clientId: DecryptedData?.clientId,
          timeZone: momentTimezone.tz.guess(),
          enrollmentPosition: DecryptedData?.enrollmentPosition
        },
      })
    },
    onError: (err) => setErrorMsg(err.message),
  })

  const [getClientAllowedComponents] = useLazyQuery(GET_CLIENT_ALLOWED_COMPONENTS, {
    onCompleted: (data) => {
      setPowerUp(data?.getClientAllowedComponents.includes('powerUpLanguageSetup'))
    },
    fetchPolicy: GLOBAL_CONSTANTS.networkOnly,
  })

  //refs
  const signatureRef = useRef<SignatureCanvas>(null)

  //effects
  useEffect(() => {
    const urlParams: any = queryParams.get('pincode')
    const decryptedData = JSON.parse(
      AES.decrypt(urlParams.replaceAll(' ', '+'), 'gDdoxYdfT5XCJw0y').toString(enc.Utf8)
    )
    setdecryptedData(decryptedData)
    getCustomerInfo({
      variables: {
        pincode: decryptedData?.pincode,
        tpvId: decryptedData?.tpvId,
        clientId: decryptedData?.clientId,
        enrollmentPosition: decryptedData?.enrollmentPosition
      },
    })
    getClientAllowedComponents()
    const startTime = moment(decryptedData?.linkCreationTime) // Current time
    const endTime = moment() // Current time + 1 hour    // Calculate the time difference in milliseconds
    const timeDifferenceMilliseconds = endTime.diff(startTime)
    if (moment.duration(timeDifferenceMilliseconds).asMinutes() > 60 && decryptedData?.pincode) {
      setErrorMsg('Contract link is expired. Please contact your agent')
    }
  }, [])
  const { coords, isGeolocationAvailable, isGeolocationEnabled } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: false,
      maximumAge: 0,
      timeout: Infinity,
    },
    watchLocationPermissionChange: true,
  })
  //handlers
  const handleSubmit = () => {
    setSubmitting(true)
      ; (async () => {
        const res = await fetch(`${signatureRef.current?.toDataURL()}`).then((res) => res.blob())
        const blob = new Blob([res], { type: 'image/png' })
        const file = new File(
          [blob],
          `${data?.firstName.replaceAll(' ', '')}${Date.now()}signature.png`,
          { type: 'image/png' }
        )
        const urls = await uploadFiles(
          [file],
          false,
          `customerSignature/${data?.firstName.replaceAll(' ', '')}${Date.now()}`
        )
        submitContract({
          variables: {
            updateContractInputs: {
              pincode: DecryptedData.pincode,
              customerLatitude: String(coords?.latitude),
              customerLongitude: String(coords?.longitude),
              signatureUrl: urls[0],
              timeZone: momentTimezone.tz.guess(),
              tpvId: DecryptedData?.tpvId,
              clientId: DecryptedData?.clientId,
              enrollmentPosition: DecryptedData?.enrollmentPosition
            },
          },
        })
      })()
  }

  // Helper function to check if location data is available
  const hasValidCoordinates = () => {
    return coords?.latitude && coords?.longitude &&
      typeof coords.latitude === 'number' &&
      typeof coords.longitude === 'number'
  }

  //return statements
  if (loading || dataLoading) {
    return <SkeletonLoader />
  }

  // Check if geolocation is not available at all
  if (!isGeolocationAvailable) {
    return (
      <Container>
        <div className='text-center alert alert-danger mt-5'>
          <h4 className='fs7'>Can't do TPV from this device</h4>
        </div>
      </Container>
    )
  }

  // Check if geolocation is not enabled or coordinates are not available
  if (!isGeolocationEnabled || !hasValidCoordinates()) {
    return (
      <Container>
        <div className='text-center alert alert-warning mt-5'>
          <h4 className='fs7'>
            To continue, please allow location in the browser settings and reopen the link
          </h4>
        </div>
      </Container>
    )
  }

  if (errorMsg) {
    return (
      <Container>
        <div className='text-center alert alert-danger mt-5'>
          <h4 className='fs7'> {errorMsg}</h4>
        </div>
      </Container>
    )
  }

  if (successMsg) {
    return (
      <Container>
        <div className='text-center alert alert-success mt-5'>
          <h4 className='fs7'> {successMsg}</h4>
        </div>
      </Container>
    )
  }

  if (Boolean(data)) {
    return (
      <Container>
        <KTCard>
          <Container className='my-4'>
            <ImagePreview
              url={
                data?.brandLogo
                  ? `${process.env.REACT_APP_IMAGE_BASE_URL}${new URL(
                    data.brandLogo
                  ).pathname.substring(1)}`
                  : `${process.env.REACT_APP_IMAGE_BASE_URL}${data?.client?.logoUrl}`
              }
              canRemove={false}
              width={100}
              height={100}
            />
            <table className='table table-rounded table-striped border gy-7 gs-7'>
              <tbody>
                <tr>
                  <td className='fw-bold'>Customer Name</td>
                  <td>
                    {checkEmptyData(data?.firstName)} {data?.lastName}
                  </td>
                </tr>
                <tr>
                  <td className='fw-bold'>Customer Email</td>
                  <td>{checkEmptyData(data?.emailId)}</td>
                </tr>
                <tr>
                  <td className='fw-bold'>Customer Service Address</td>
                  <td>
                    {data?.serviceAddress1}, {data?.serviceCity}, {data?.serviceState},{' '}
                    {data?.serviceCountry}
                  </td>
                </tr>
                <tr>
                  <td className='fw-bold'>Customer Billing Address</td>
                  <td>
                    {data?.billingAddress1}, {data?.billingCity}, {data?.billingCity},{' '}
                    {data?.billingCountry}
                  </td>
                </tr>
                <tr>
                  <td className='fw-bold'>Customer Phone Number</td>
                  <td>{checkEmptyData(data?.primaryPhoneNumber)}</td>
                </tr>

                {(data?.electricEnrollment?.productType === 'Electric' ||
                  data?.electricEnrollment?.productType === 'Dual') && (
                    <>
                      <tr>
                        <td className='fw-bold'>Electric Product</td>
                        <td>{checkEmptyData(data?.electricEnrollment?.product?.name ?? null)}</td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Electric UAN</td>
                        <td>{checkEmptyData(data?.electricEnrollment?.utilityAccountNumber)}</td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Electric Rate</td>
                        <td>
                        {!([null,0].includes(data?.electricEnrollment?.rate))&&(
                          <>
                            {Boolean(data?.electricEnrollment?.rate) && '$'}{' '}
                          {checkEmptyData(data?.electricEnrollment?.rate)}
                          </>
                        )}
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Electric Term</td>
                        <td> {checkEmptyData(data?.electricEnrollment?.term)}</td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Electric Service Fee</td>
                        <td>
                          {Boolean(data?.electricEnrollment?.serviceFee) && '$'}{' '}
                          {checkEmptyData(data?.electricEnrollment?.serviceFee)}
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Electric Termination Fee</td>
                        <td>
                          {Boolean(data?.electricEnrollment?.earlyTerminationFee) && '$'}{' '}
                          {checkEmptyData(data?.electricEnrollment?.earlyTerminationFee)}
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Electric Product Code</td>
                        <td>{checkEmptyData(data?.electricEnrollment?.code)}</td>
                      </tr>
                    </>
                  )}
                {(data?.gasEnrollment?.productType === 'Gas' ||
                  data?.gasEnrollment?.productType === 'Dual') && (
                    <>
                      <tr>
                        <td className='fw-bold'>Gas Product</td>
                        <td>{checkEmptyData(data?.gasEnrollment?.product?.name ?? null)}</td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Gas UAN</td>
                        <td>{checkEmptyData(data?.gasEnrollment?.utilityAccountNumber)}</td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Gas Rate</td>
                        <td>
                         {!([null,0].includes(data?.gasEnrollment?.rate))&&(
                          <>
                            {Boolean(data?.gasEnrollment?.rate) && '$'}{' '}
                          {checkEmptyData(data?.gasEnrollment?.rate)}
                          </>
                        )}
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Gas Term</td>
                        <td> {checkEmptyData(data?.gasEnrollment?.term)}</td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Gas Service Fee</td>
                        <td>
                          {Boolean(data?.gasEnrollment?.serviceFee) && '$'}{' '}
                          {checkEmptyData(data?.gasEnrollment?.serviceFee)}
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Gas Termination Fee</td>
                        <td>
                          {Boolean(data?.gasEnrollment?.earlyTerminationFee) && '$'}{' '}
                          {checkEmptyData(data?.gasEnrollment?.earlyTerminationFee)}
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Gas Product Code</td>
                        <td> {checkEmptyData(data?.gasEnrollment?.code)}</td>
                      </tr>
                    </>
                  )}
              </tbody>
            </table>

            <Row>
              {data?.tpvs?.length ? (
                data.tpvs.map((tpv: any) =>
                  tpv.tpvContracts.length ? (
                    tpv.tpvContracts.map((file: any) => (
                      <Col lg={6} key={file.fileUrl} className='mb-3'>
                        <p className='fs-4 fw-bold my-5'>{renderFileHeader(file.position)}</p>
                        <PdfViewer
                          url={file.fileUrl}
                          previewSettings={GLOBAL_CONSTANTS.previewSettings}
                          type={isValidURL(file.fileUrl) ? '' : 'contract'}
                        />
                      </Col>
                    ))
                  ) : (
                    <div className='text-center'>No files found</div>
                  )
                )
              ) : (
                <div className='text-center'>No files found</div>
              )}
            </Row>

            <div className='d-flex flex-column align-items-center justify-content-center my-5'>
              <div>
                <div className='mb-3'>
                  <p className='d-inline me-2 fs-6 fw-semibold'>Please add your signature</p>
                  <button
                    className='btn btn-sm btn-secondary'
                    onClick={() => {
                      signatureRef.current?.clear()
                      setIsSigned(false)
                    }}
                  >
                    Clear Signature
                  </button>
                </div>
                <SignatureCanvas
                  ref={signatureRef}
                  penColor='black'
                  canvasProps={{
                    className: 'sigCanvas',
                    style: { border: '1px solid #000', height: '150px', width: '100%' },
                  }}
                  throttle={10}
                  minWidth={0.5}
                  maxWidth={1.5}
                  onBegin={(e) => setIsSigned(true)}
                  clearOnResize={false}
                />
              </div>
            </div>
            <div className='text-center'>
              <div className='form-check form-check-custom form-check-solid justify-content-center mb-2'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='flexCheckDefault'
                  checked={consentChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setIsConsentChecked(e.target.checked)
                  }
                />
                <label className='form-check-label' htmlFor='flexCheckDefault'>
                  {data?.tpvs?.[0]?.language === 'English' && PowerUp
                    ? `I have previewed and read my contract, accepted the terms and conditions and agreed to choose ${data?.viewBrandInfoOverClient
                      ? data?.electricEnrollment?.brandType?.name
                        ? data?.electricEnrollment?.brandType?.name
                        : data?.gasEnrollment?.brandType?.name
                      : data?.client?.name
                    } as my supplier`
                    : data?.tpvs?.[0]?.language === 'Spanish' && PowerUp
                      ? `He revisado y leído mi contrato, acepto los términos y condiciones y estoy de acuerdo en elegir a ${data?.viewBrandInfoOverClient
                        ? data?.electricEnrollment?.brandType?.name
                          ? data?.electricEnrollment?.brandType?.name
                          : data?.gasEnrollment?.brandType?.name
                        : data?.client?.name
                      } como mi proveedor`
                      : `I have previewed and read my contract, accepted the terms and conditions and agreed to choose ${data?.viewBrandInfoOverClient
                        ? data?.electricEnrollment?.brandType?.name
                          ? data?.electricEnrollment?.brandType?.name
                          : data?.gasEnrollment?.brandType?.name
                        : data?.client?.name
                      } as my supplier`}
                </label>
              </div>
              <div className='form-check form-check-custom form-check-solid justify-content-center mb-2'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='flexCheckDefault2'
                  checked={optinChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOptInChecked(e.target.checked)
                  }
                />
                <label className='form-check-label' htmlFor='flexCheckDefault2'>
                  {data?.client?.clientOptInMessage}
                </label>
              </div>
              <button
                className='btn btn-primary text-center'
                onClick={(e) => {
                  e.stopPropagation()
                  handleSubmit()
                }}
                disabled={
                  !consentChecked ||
                  !isSigned ||
                  !optinChecked ||
                  !hasValidCoordinates()
                }
              >
                {submitting ? 'Submitting Contract' : 'Submit'}
                {submitting && (
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                )}
              </button>
            </div>
          </Container>
        </KTCard>
      </Container>
    )
  }

  return (
    <Container>
      <div className='text-center alert alert-danger mt-5'>
        <h4 className='fs7'> Something went wrong!</h4>
      </div>
    </Container>
  )
}

export default Contract
